<template>
  <component
    :is="linkTag"
    ref="rootRef"
    v-ripple="ripple"
    class="sc-item"
    :class="classes"
    :tabindex="tabIndex"
    role="tab"
    :aria-selected="isActive ? 'true' : 'false'"
    :aria-disabled="disable ? 'true' : null"
    :href="linkAttrs.href || undefined"
    :target="linkAttrs.target || undefined"
    v-bind="$attrs"
    @click="onClick"
    @keydown="onKeydown"
  >
    <div
      ref="blurTargetRef"
      class="sc-item--focus-helper"
      tabindex="-1"
    />
    <div
      ref="tabIndicatorRef"
      :class="['sc-item--indicator', $tabs.tabProps.value.indicatorClass]"
    />
    <div
      class="sc-item--content"
      :class="innerClass"
    >
      <q-icon
        v-if="icon"
        class="sc-item--icon"
        :name="icon"
      />
      <slot>
        <div
          v-if="label"
          class="sc-item--label"
        >
          {{ label }}
        </div>
      </slot>
      <template v-if="alert">
        <q-icon
          v-if="alertIcon"
          class="sc-item--alert-icon"
          :color="alert !== true ? alert : null"
          :name="alertIcon"
        />
        <div
          v-else
          class="sc-item--alert"
          :class="alert !== true ? `text-${alert}` : null"
        />
      </template>
    </div>
  </component>
</template>

<script setup>
import { useId, inject, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { cx } from '@/helpers'

defineOptions({ name: 'RouteSegment', inheritAttrs: false })
const emit = defineEmits(['click', 'keydown'])
const props = defineProps({
  // Tab props (shared with Segment)
  icon: { type: String, default: null },
  label: { type: [Number, String], default: null },
  alert: { type: [Boolean, String], default: false },
  alertIcon: { type: String, default: null },
  name: { type: [Number, String], default: () => `t_${useId()}` },
  tabindex: { type: [String, Number], default: null },
  disable: { type: Boolean, default: false },
  contentClass: { type: String, default: null },
  ripple: { type: [Boolean, Object], default: true },

  // Router link props
  to: { type: [String, Object], default: null },
  replace: { type: Boolean, default: false },
  href: { type: String, default: null },
  target: { type: String, default: null },
  exact: { type: Boolean, default: false },
  activeClass: { type: String, default: 'is-active' },
  exactActiveClass: { type: String, default: 'q-router-link--exact-active' }
})

// Inject tabs context
const $tabs = inject('segmentedTabs', null)
if ($tabs === null) {
  console.error('RouteSegment component must be a child of SegmentedControl')
}

// Router
let $router = null
let $route = null
try {
  $router = useRouter()
  $route = useRoute()
} catch {
  console.error('RouteSegment requires Vue Router to be installed')
}

// Utility functions
function isDeepEqual (a, b) {
  if (a === b) return true
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every(k => isDeepEqual(a[k], b[k]))
}

function includesParams (outer, inner) {
  for (const key in inner) {
    const innerVal = inner[key]
    const outerVal = outer[key]
    if (typeof innerVal === 'string') {
      if (innerVal !== outerVal) return false
    } else if (
      !Array.isArray(outerVal)
      || outerVal.length !== innerVal.length
      || innerVal.some((v, i) => v !== outerVal[i])
    ) {
      return false
    }
  }
  return true
}

function isSameLocationParam (a, b) {
  if (Array.isArray(a)) {
    return Array.isArray(b)
      ? a.length === b.length && a.every((v, i) => v === b[i])
      : a.length === 1 && a[0] === b
  }
  return Array.isArray(b) ? b.length === 1 && b[0] === a : a === b
}

function isSameRouteLocationParams (a, b) {
  if (Object.keys(a).length !== Object.keys(b).length) return false
  for (const key in a) {
    if (!isSameLocationParam(a[key], b[key])) return false
  }
  return true
}

// Router link state
const hasHrefLink = computed(() => props.href !== null)
const hasRouterLinkProps = computed(() => (
  $router !== null
  && !hasHrefLink.value
  && props.to !== null
  && props.to !== ''
))

const resolvedLink = computed(() => {
  if (!hasRouterLinkProps.value) return null
  try {
    return $router.resolve(props.to)
  } catch {
    return null
  }
})

const hasRouterLink = computed(() => resolvedLink.value !== null)
const hasLink = computed(() => hasHrefLink.value || hasRouterLink.value)

const linkTag = computed(() => hasLink.value ? 'a' : 'div')

const linkAttrs = computed(() => {
  if (hasHrefLink.value) {
    return { href: props.href, target: props.target }
  }
  if (hasRouterLink.value) {
    return { href: resolvedLink.value.href, target: props.target }
  }
  return {}
})

// Active-index: where our resolved route appears in the current route's matched array
const linkActiveIndex = computed(() => {
  if (!hasRouterLink.value || !$route) return -1
  const { matched } = resolvedLink.value
  const routeMatched = matched[matched.length - 1]
  if (!routeMatched) return -1
  return $route.matched.findIndex(
    m => (routeMatched.aliasOf || routeMatched) === (m.aliasOf || m)
  )
})

const linkIsActive = computed(() => (
  linkActiveIndex.value !== -1
  && includesParams($route.params, resolvedLink.value.params)
))

const linkIsExactActive = computed(() => (
  linkIsActive.value
  && linkActiveIndex.value === $route.matched.length - 1
  && isSameRouteLocationParams($route.params, resolvedLink.value.params)
))

// Extra CSS classes reflecting router link active state (independent of tab model)
const linkClass = computed(() => {
  if (!hasRouterLink.value) return ''
  if (linkIsExactActive.value) return ` ${props.exactActiveClass} ${props.activeClass}`
  if (!props.exact && linkIsActive.value) return ` ${props.activeClass}`
  return ''
})

// Tab registration data─
// routeData exposes the router link state so SegmentedControl can run its own
// route-matching logic (updateActiveRoute) independently of the tab model.
const routeData = {
  exact: computed(() => props.exact),
  hasRouterLink,
  linkIsActive,
  linkIsExactActive,
  resolvedLink,
  linkClass
}

// Refs
const blurTargetRef = ref(null)
const rootRef = ref(null)
const tabIndicatorRef = ref(null)

const tabData = {
  name: computed(() => props.name),
  rootRef,
  tabIndicatorRef,
  routeData
}

// Tab UI
const ripple = computed(() => (
  props.disable === true || props.ripple === false
    ? false
    : Object.assign(
        { keyCodes: ['Enter', 13, 32], early: true },
        props.ripple === true ? {} : props.ripple
      )
))

const isActive = computed(() => $tabs.currentModel.value === props.name)

const classes = computed(() => {
  const { activeClass: tabActiveClass, activeColor, activeBgColor } = $tabs.tabProps.value
  return cx(
    isActive.value
      ? [
          'is-active',
          tabActiveClass,
          activeColor && `text-${activeColor}`,
          activeBgColor && `bg-${activeBgColor}`
        ]
      : 'is-inactive',
    linkClass.value,
    props.icon && props.label && !$tabs.tabProps.value.inlineLabel && 'is-full',
    props.disable ? 'is-disabled' : 'is-focusable is-hoverable'
  )
})

const innerClass = computed(() => cx(
  $tabs.tabProps.value.inlineLabel ? 'is-inline' : 'is-stack',
  props.contentClass
))

const tabIndex = computed(() => (
  (props.disable || $tabs.hasFocus.value || (isActive.value === false && $tabs.hasActiveTab.value))
    ? -1
    : props.tabindex ?? 0
))

// Navigation
// Each call to go() gets a unique ID so verifyRouteModel knows whether the
// ensuing route change was triggered by this tab and should not re-evaluate.
let navigatingUid = 0

const navigateToRouterLink = (e, { returnRouterError = false, to: navTo = props.to, replace: navReplace = props.replace } = {}) => {
  if (props.disable === true) {
    e.preventDefault()
    return Promise.resolve(false)
  }
  // Don't intercept modifier-key clicks or non-primary buttons (let browser handle)
  if (
    e.metaKey || e.altKey || e.ctrlKey || e.shiftKey
    || (e.button !== undefined && e.button !== 0)
    || props.target === '_blank'
  ) {
    return Promise.resolve(false)
  }
  e.preventDefault()
  const promise = $router[navReplace === true ? 'replace' : 'push'](navTo)
  return returnRouterError === true
    ? promise
    : promise.then(() => {}).catch(() => {})
}

const onClick = (e, kbd) => {
  if (!kbd && !e?.qAvoidFocus) {
    blurTargetRef.value?.focus()
  }

  if (props.disable === true) {
    // Prevent native navigation even when disabled
    if (hasRouterLink.value) e.preventDefault()
    return
  }

  if (!hasRouterLink.value) {
    // No router link — just emit, no model update (caller may handle it)
    emit('click', e)
    return
  }

  // go() is also exposed in the click event so callers can override navigation opts
  const go = (opts = {}) => {
    let hardError
    // Only suppress the route watcher if we're navigating to this tab's own route
    const isSameTo = opts.to === undefined || isDeepEqual(opts.to, props.to)
    const reqId = isSameTo
      ? ($tabs.avoidRouteWatcher = ++navigatingUid)
      : null

    return navigateToRouterLink(e, { ...opts, returnRouterError: true })
      .catch(err => {
        hardError = err
      })
      .then(softError => {
        if (reqId !== null && reqId === $tabs.avoidRouteWatcher) {
          $tabs.avoidRouteWatcher = false
          // Only update model if navigation fully succeeded (no hard errors, and
          // the only soft error allowed is "Avoided redundant navigation")
          if (
            hardError === undefined && (
              softError === undefined
              || softError.message?.startsWith('Avoided redundant navigation') === true
            )
          ) {
            $tabs.updateModel({ name: props.name })
          }
        }
        if (opts.returnRouterError === true) {
          return hardError !== undefined ? Promise.reject(hardError) : softError
        }
      })
  }

  emit('click', e, go)
  if (e.defaultPrevented !== true) go()
}

// Keyboard
const navKeys = ['Home', 'End', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown']

const shouldIgnoreKey = e => (
  e !== Object(e)
  || e.isComposing === true
  || e.qKeyEvent === true
)

const stopAndPrevent = e => {
  e.cancelable && e.preventDefault()
  e.stopPropagation()
}

const onKeydown = e => {
  if (['Enter', ' '].includes(e.key)) {
    onClick(e, true)
    return
  }
  if (
    !shouldIgnoreKey(e)
    && navKeys.includes(e.key)
    && !e.altKey
    && !e.metaKey
  ) {
    if ($tabs.onKbdNavigate(e.key, rootRef.value) === true) stopAndPrevent(e)
  }
  emit('keydown', e)
}

// Route watchers
// Tell SegmentedControl to re-evaluate which tab should be active whenever
// this tab's route-identifying props change.
watch(
  () => `${props.name} | ${props.exact} | ${resolvedLink.value?.href ?? ''}`,
  () => {
    $tabs.verifyRouteModel()
  }
)

// Lifecycle
onMounted(() => {
  $tabs.registerTab(tabData)
})
onBeforeUnmount(() => {
  $tabs.unregisterTab(tabData)
})
</script>
